import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext.jsx';
import { Badge, Empty, Pagination, STATUS_TONE, fmtDate, label } from '../components/ui.jsx';
import { t } from '../i18n/index.jsx';
import OfferForm, { OFFER_STATUSES, blankOffer } from '../components/forms/OfferForm.jsx';
import { IconPlus } from '../components/icons.jsx';
import DataTable from '../components/DataTable.jsx';
import ExportButton from '../components/ExportButton.jsx';
import { cachedFetch, invalidate } from '../api/cache.js';

const OFFER_TONE = { ...STATUS_TONE, negotiating: 'amber', accepted: 'green' };
const statusTemplate = (row) => <Badge tone={OFFER_TONE[row.status] || 'gray'}>{label(row.status)}</Badge>;
const amountTemplate = (row) => (row.amount ? `${Number(row.amount).toLocaleString()} ${row.currency}` : '—');

export default function Offers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canEdit = user.role === 'admin' || user.role === 'manager';
  const [list, setList] = useState(null);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    params.set('page', String(page));
    params.set('per_page', '20');
    const path = `/offers?${params}`;
    const res = await cachedFetch(path, () => api.get(path));
    setList(res.data);
    setMeta(res.meta || null);
  }, [status, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [status]);
  useEffect(() => {
    cachedFetch('/companies?type=client', () => api.get('/companies?type=client')).then((r) => setClients(r.data)).catch(() => setClients([]));
    cachedFetch('/projects', () => api.get('/projects')).then((r) => setProjects(r.data)).catch(() => setProjects([]));
  }, []);

  const columns = [
    { field: 'title', headerText: t('Title'), width: 200 },
    { field: 'client_name', headerText: t('Client'), width: 160 },
    { field: 'project_name', headerText: t('Project'), width: 160 },
    { headerText: t('Status'), width: 120, template: statusTemplate },
    { headerText: t('Amount'), width: 130, template: amountTemplate },
    { field: 'valid_until', headerText: t('Valid until'), width: 120, template: (r) => fmtDate(r.valid_until) },
  ];

  return (
    <div className="page fade-in">
      <div className="page-head">
        <h1 className="page-title">{t('Offers')}</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <ExportButton
            filename="offers"
            rows={list || []}
            columns={[
              { header: t('Title'), field: 'title' },
              { header: t('Client'), field: 'client_name' },
              { header: t('Project'), field: 'project_name' },
              { header: t('Status'), field: 'status' },
              { header: t('Amount'), value: (r) => r.amount ?? '' },
              { header: t('Valid until'), field: 'valid_until' },
            ]}
            disabled={!list?.length}
          />
          {canEdit && (
            <button type="button" className="btn primary" onClick={() => setEditing({ ...blankOffer })}>
              <IconPlus width={15} height={15} /> {t('New offer')}
            </button>
          )}
        </div>
      </div>

      <div className="toolbar">
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">{t('All statuses')}</option>
          {OFFER_STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
        </select>
      </div>

      {!list ? (
        <DataTable data={[]} columns={columns} loading />
      ) : list.length === 0 ? (
        <Empty>{t('No offers yet.')}</Empty>
      ) : (
        <DataTable
          data={list}
          columns={columns}
          onRowClick={canEdit ? setEditing : undefined}
          onRowAction={canEdit ? setEditing : null}
          actionLabel={t('Open')}
        />
      )}

      <Pagination meta={meta} onPage={setPage} />

      {editing && (
        <OfferForm
          initial={editing}
          projects={projects}
          clients={clients}
          isAdmin={user.role === 'admin'}
          onClose={() => setEditing(null)}
          onSaved={() => { invalidate('/offers'); setEditing(null); load(); }}
          onAccepted={(contractId) => {
            invalidate('/offers'); invalidate('/contracts');
            setEditing(null);
            navigate('/contracts');
          }}
        />
      )}
    </div>
  );
}
